/**
 * IndexedDB Availability Checker
 *
 * Detects if IndexedDB is available and working in the current environment.
 * Handles edge cases like:
 * - Private/incognito mode (Firefox blocks IndexedDB completely)
 * - Browser policy restrictions
 * - Older browsers without IndexedDB support
 * - Quota exceeded scenarios
 *
 * Part of: IndexedDB Phase 3.6 Migration - Week 0 Safety Infrastructure
 * Created: 2026-01-30
 */

// Cache the availability check result to avoid repeated testing
let cachedResult = null;
let checkPromise = null;

/**
 * Check if IndexedDB is available and functional
 *
 * @returns {Promise<{available: boolean, reason?: string}>}
 */
export async function checkIndexedDBAvailability() {
  // Return cached result if we've already checked
  if (cachedResult !== null) {
    return cachedResult;
  }

  // Return in-progress check if one is already running
  if (checkPromise) {
    return checkPromise;
  }

  // Start new check
  checkPromise = performAvailabilityCheck();

  try {
    const result = await checkPromise;
    cachedResult = result;
    return result;
  } finally {
    checkPromise = null;
  }
}

/**
 * Perform the actual availability check
 * @private
 */
async function performAvailabilityCheck() {
  // Step 1: Check if IndexedDB exists in window
  if (!window.indexedDB) {
    return {
      available: false,
      reason: 'IndexedDB not supported in this browser'
    };
  }

  // Step 2: Try to open a test database
  try {
    const testDBName = '__idb_availability_test__';
    const testStoreName = 'test';

    // Open test database
    const db = await new Promise((resolve, reject) => {
      const request = window.indexedDB.open(testDBName, 1);

      // Timeout after 5 seconds
      let openTimeout = setTimeout(() => {
        reject(new Error('IndexedDB open timeout'));
      }, 5000);

      request.onerror = () => {
        clearTimeout(openTimeout);
        reject(new Error(request.error?.message || 'Unknown error opening IndexedDB'));
      };

      request.onsuccess = () => {
        clearTimeout(openTimeout);
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(testStoreName)) {
          db.createObjectStore(testStoreName);
        }
      };
    });

    // Step 3: Try to write and read test data
    try {
      await new Promise((resolve, reject) => {
        const transaction = db.transaction([testStoreName], 'readwrite');
        const store = transaction.objectStore(testStoreName);

        const testKey = 'test_key';
        const testValue = { timestamp: Date.now() };

        // Timeout after 3 seconds
        let writeReadTimeout = setTimeout(() => {
          reject(new Error('IndexedDB write/read timeout'));
        }, 3000);

        // Write test data
        const putRequest = store.put(testValue, testKey);

        putRequest.onerror = () => {
          clearTimeout(writeReadTimeout);
          reject(new Error('Failed to write test data'));
        };

        putRequest.onsuccess = () => {
          // Read test data back
          const getRequest = store.get(testKey);

          getRequest.onerror = () => {
            clearTimeout(writeReadTimeout);
            reject(new Error('Failed to read test data'));
          };

          getRequest.onsuccess = () => {
            clearTimeout(writeReadTimeout);
            if (getRequest.result) {
              resolve();
            } else {
              reject(new Error('Test data not found after write'));
            }
          };
        };

        transaction.onerror = () => {
          clearTimeout(writeReadTimeout);
          reject(new Error('Transaction failed'));
        };
      });
    } finally {
      // Clean up: close and delete test database
      db.close();
      await new Promise((resolve) => {
        const deleteRequest = window.indexedDB.deleteDatabase(testDBName);
        deleteRequest.onsuccess = resolve;
        deleteRequest.onerror = resolve; // Continue even if delete fails
        deleteRequest.onblocked = resolve;
      });
    }

    // Step 4: All checks passed
    return {
      available: true
    };

  } catch (error) {
    // Determine specific failure reason
    const errorMessage = error.message || String(error);

    // Firefox private mode throws SecurityError
    if (errorMessage.includes('SecurityError') || errorMessage.includes('security')) {
      return {
        available: false,
        reason: 'IndexedDB blocked (private browsing mode or security policy)'
      };
    }

    // Quota exceeded
    if (errorMessage.includes('quota') || errorMessage.includes('QuotaExceededError')) {
      return {
        available: false,
        reason: 'IndexedDB quota exceeded'
      };
    }

    // Generic failure
    return {
      available: false,
      reason: `IndexedDB check failed: ${errorMessage}`
    };
  }
}

/**
 * Clear the cached availability result
 * Useful for testing or if storage mode changes
 */
export function clearAvailabilityCache() {
  cachedResult = null;
  checkPromise = null;
}

/**
 * Check if we're likely in private browsing mode
 * This is a heuristic check, not 100% reliable
 *
 * @returns {boolean}
 */
export function isLikelyPrivateBrowsing() {
  // Check common indicators
  try {
    // Firefox private mode: indexedDB exists but can't be opened
    if (navigator.userAgent.includes('Firefox')) {
      // This is checked by the full availability test
      return false; // Let the real check handle it
    }

    // Chrome/Edge incognito: FileSystem API restricted
    if ('webkitRequestFileSystem' in window) {
      return false; // Has FileSystem API, probably not incognito
    }

    // Safari private mode: localStorage quota is 0
    if ('localStorage' in window) {
      try {
        localStorage.setItem('__private_test__', '1');
        localStorage.removeItem('__private_test__');
        return false; // localStorage works, probably not private
      } catch (e) {
        return true; // localStorage blocked, likely private mode
      }
    }

    return false;
  } catch (e) {
    return false; // Can't determine, assume not private
  }
}

/**
 * Get a user-friendly error message based on availability check
 *
 * @param {Object} availabilityResult - Result from checkIndexedDBAvailability()
 * @returns {string} - User-friendly error message
 */
export function getAvailabilityErrorMessage(availabilityResult) {
  if (availabilityResult.available) {
    return null;
  }

  const reason = availabilityResult.reason || 'Unknown reason';

  if (reason.includes('private browsing') || reason.includes('security policy')) {
    return 'IndexedDB is not available in private browsing mode. Please use a regular browser window for offline data storage.';
  }

  if (reason.includes('quota exceeded')) {
    return 'Storage quota exceeded. Please free up some space or contact support.';
  }

  if (reason.includes('not supported')) {
    return 'Your browser does not support offline data storage. Please use a modern browser (Chrome, Firefox, Edge, Safari).';
  }

  return `Offline data storage is not available: ${reason}`;
}

// Expose to window for debugging in development
if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
  window.indexedDBAvailability = {
    check: checkIndexedDBAvailability,
    clearCache: clearAvailabilityCache,
    isPrivate: isLikelyPrivateBrowsing,
    getErrorMessage: getAvailabilityErrorMessage
  };
}
